import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ApiError, setUnauthorizedHandler } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { registerSessionCleanup } from '@/stores/sessionCleanup';
import { useThemeStore } from '@/stores/theme';
import { applyBgAttrs } from '@/stores/preferences';
// Self-hosted fonturi (CSP-safe) — evităm Google Fonts blocat de CSP strict
import '@fontsource/geist-sans/300.css';
import '@fontsource/geist-sans/400.css';
import '@fontsource/geist-sans/500.css';
import '@fontsource/geist-sans/600.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
import '@fontsource/instrument-serif/400.css';
import '@fontsource/instrument-serif/400-italic.css';
import './index.css';
import './i18n';
import DialogHost from '@/components/DialogHost';

useThemeStore.getState().initialize();
applyBgAttrs();
// Hidratare sincronă înainte de render — refresh-ul menține sesiunea dacă
// sessionStorage conține un blob valid (tab-scoped, curățat la close tab).
useAuthStore.getState().hydrate();
// Global 401 → clearSession → ProtectedRoute redirect /login.
setUnauthorizedHandler(() => {
  useAuthStore.getState().clearSession();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // O reîncercare, dar numai pentru erori de rețea sau 5xx: un 403, 404 sau
      // 429 nu se rezolvă repetând cererea (iar 429 s-ar agrava).
      retry: (failureCount, error) =>
        failureCount < 1 && !(error instanceof ApiError && error.status < 500),
      refetchOnWindowFocus: false,
    },
  },
});

// La încheierea sesiunii golim cache-ul: conține titluri, username-uri și
// blob-uri criptate ale userului care tocmai a ieșit.
registerSessionCleanup(() => queryClient.clear());

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <DialogHost />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
